import { Component, OnInit, Inject, DOCUMENT, ChangeDetectionStrategy } from '@angular/core'

import { Subscription, timer } from 'rxjs'
import { ErrorMessage, UiService } from 'src/app/services/ui.service'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { datadogRum } from '@datadog/browser-rum'
import { NavigationEnd, Router } from '@angular/router'
import { LoggerService } from 'src/app/account/services/logger.service'

@Component({
  selector: 'app-root',
  templateUrl: './ui-shell.component.html',
  styleUrls: ['./ui-shell.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UiShell implements OnInit {
  errorHeading: string = null
  errorMessage: string = null
  // Drives the shared toast's visual variant — 'success' uses the small pill,
  // anything else (including legacy unset) uses the existing error box.
  toastSeverity: 'error' | 'success' = 'error'
  // When true, the toast won't auto-dismiss; the user must click its close button.
  toastSticky: boolean = false
  private hideErrorTimerSubscription: Subscription = null
  private fadeErrorTimerSubscription: Subscription = null

  constructor(
    private uiService: UiService,
    private router: Router,
    private logger: LoggerService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        datadogRum.startView(event.urlAfterRedirects) // Track route changes
        this.logger.info(`User navigated to: ${event.urlAfterRedirects}`)
      }
    })
  }

  ngOnInit() {
    // Initialize font loading optimization
    this.initializeFontLoading()

    this.uiService.errorStream.subscribe((value: ErrorMessage) => {
      if (subscriptionActive(this.hideErrorTimerSubscription)) {
        this.hideErrorTimerSubscription.unsubscribe()
      }
      if (subscriptionActive(this.fadeErrorTimerSubscription)) {
        this.fadeErrorTimerSubscription.unsubscribe()
      }

      this.toastSeverity = value.severity ?? 'error'
      this.toastSticky = !!value.sticky && this.toastSeverity !== 'success'

      if (this.toastSeverity === 'success') {
        // Short-lived confirmation pill (e.g., "Copied!") — no heading, no console log.
        this.errorHeading = null
        this.errorMessage = value.message
      } else {
        console.error(value.detailedMessage)
        // Use the message field as a heading when it's a short, curated string. Raw HttpErrors
        // carry an http-framework message ("Http failure response for /api/...: 500..."), which
        // is too long and noisy for a heading — fall back to "Oops!" in that case.
        const heading = value.message && value.message.length <= 60 ? value.message : 'Oops!'
        this.errorHeading = heading
        this.errorMessage = value.detailedMessage
      }

      // Sticky toasts stay up until the user clicks the close button; everything
      // else auto-dismisses (success pill 1.5s, error box 5s).
      if (this.toastSticky) return

      const visibleMs = this.toastSeverity === 'success' ? 1500 : 5000
      this.hideErrorTimerSubscription = timer(visibleMs).subscribe((_) => {
        // set a timer to clear the error message after the fade completes.
        // this timing has to be matched to the timing of the animation in
        // the css!
        this.fadeErrorTimerSubscription = timer(500).subscribe((_) => {
          this.errorMessage = null
          this.errorHeading = null
        })
      })
    })
  }

  private initializeFontLoading() {
    // Add fonts-loading class initially
    this.document.body.classList.add('fonts-loading')

    // Modern approach: Use Font Loading API
    if (this.document.fonts) {
      // Check if fonts are already loaded
      this.document.fonts.ready.then(() => {
        this.checkSpecificFonts()
      })
    } else {
      // Fallback for very old browsers
      this.fallbackFontCheck()
    }
  }

  private checkSpecificFonts() {
    try {
      // Check if Source Sans 3 is available using FontFace.load()
      this.document.fonts
        .load('12px "Source Sans 3"')
        .then(() => {
          this.onFontsLoaded()
        })
        .catch(() => {
          // Font failed to load, use fallback timing
          this.fallbackFontCheck()
        })
    } catch (error) {
      // FontFace.load not supported, use fallback
      this.fallbackFontCheck()
    }
  }

  private fallbackFontCheck() {
    // Simple timeout for older browsers - most fonts load within 2-3 seconds
    setTimeout(() => {
      this.onFontsLoaded()
    }, 3000)
  }

  private onFontsLoaded() {
    this.document.body.classList.remove('fonts-loading')
    this.document.body.classList.add('fonts-loaded')
  }

  get showError() {
    return !!this.errorMessage && !subscriptionActive(this.fadeErrorTimerSubscription)
  }

  get hideError() {
    return !!this.errorMessage && subscriptionActive(this.fadeErrorTimerSubscription)
  }

  // Triggered by the close button on sticky toasts. Mirrors the auto-dismiss path:
  // run the fade animation first, then clear state when it completes.
  dismissToast() {
    if (subscriptionActive(this.hideErrorTimerSubscription)) {
      this.hideErrorTimerSubscription.unsubscribe()
    }
    this.fadeErrorTimerSubscription = timer(500).subscribe((_) => {
      this.errorMessage = null
      this.errorHeading = null
      this.toastSticky = false
    })
  }
}
